import React, { FC } from 'react'
import classNames from 'classnames'
import { isNil } from 'lodash'

import { ButtonGroup, Button } from 'components/ui-kit'

import { ModalDialog, ModalBody, ModalHeader, ModalSubtitle, ModalFooter, ModalTitle } from 'components/modal'
import { SvgIcon } from 'components/icon/SvgIcon'
import { ButtonSize } from 'types/Button'

import './ConfirmationDialog.scss'

interface Props {
  onHide: () => void
  show?: boolean
  isReady?: boolean
  keyboard?: boolean
  isBodyWithoutPadding?: boolean
  onShow?: () => void
  className?: string
  bodyClassName?: string
  onConfirm?: () => void
  isPending?: boolean
  title?: React.ReactNode[] | React.ReactElement | string
  subTitle?: React.ReactNode
  showFooter?: boolean
  discardBtnLabel?: string
  confirmBtnLabel?: string
  children?: React.ReactNode[] | React.ReactElement | string
  backdrop?: string | boolean
  isCustomCloseButton?: boolean
  withCloseButton?: boolean
  isDiscardBtnShown?: boolean
  isConfirmButtonShown?: boolean
  buttonSize?: ButtonSize
  dataTestId?: string
}

/**
 * Unified Confirmation Modal Dialog
 *
 * @param props
 * @constructor
 */
export const ConfirmationDialog: FC<Props> = ({
  show,
  isReady = true,
  isBodyWithoutPadding = false,
  onHide,
  onShow = () => {},
  className,
  onConfirm,
  isPending = false,
  title = 'Confirm',
  subTitle,
  showFooter = true,
  discardBtnLabel = 'Cancel',
  isDiscardBtnShown = true,
  isConfirmButtonShown = true,
  confirmBtnLabel = 'Confirm',
  children = 'Do you confirm?',
  isCustomCloseButton = false,
  withCloseButton = true,
  buttonSize = 'big',
  backdrop = true,
  keyboard = true,
  dataTestId,
}) => (
  <ModalDialog
    // @ts-ignore
    keyboard={keyboard}
    show={show}
    onShow={onShow}
    onHide={onHide}
    className={classNames('confirmation-dialog', className)}
    backdrop={backdrop}
    data-test-id={dataTestId}>
    <ModalHeader
      closeButton={!isCustomCloseButton && withCloseButton}
      className={classNames({ 'with-sub-title': !isNil(subTitle) })}>
      <div className="titles-container">
        <ModalTitle>{title}</ModalTitle>
        {subTitle && <ModalSubtitle>{subTitle}</ModalSubtitle>}
      </div>

      {isCustomCloseButton && withCloseButton && (
        <div onClick={onHide} className="modal-close-btn">
          <SvgIcon name="close" />
        </div>
      )}
    </ModalHeader>
    <ModalBody
      className={classNames('confirmation-dialog-body', {
        'body-without-padding': isBodyWithoutPadding,
      })}>
      {children}
    </ModalBody>
    {showFooter && (
      <ModalFooter>
        <ButtonGroup>
          {isDiscardBtnShown && (
            <Button
              size={buttonSize}
              type="secondary"
              id="tests-modal-button-discard"
              onClick={onHide}
              disabled={isPending}>
              {discardBtnLabel}
            </Button>
          )}

          {isConfirmButtonShown && (
            <Button
              size={buttonSize}
              type="primary"
              id="tests-modal-button-confirm"
              pending={isPending}
              disabled={!isReady}
              onClick={onConfirm}>
              {confirmBtnLabel}
            </Button>
          )}
        </ButtonGroup>
      </ModalFooter>
    )}
  </ModalDialog>
)
